// Resolver for the native-device gateway (BL-2090): picks the Capacitor adapter
// inside the phone app, the emulated adapter in a non-production browser emulating
// it (BL-2450), and the browser adapter everywhere else, once per page.
import { BrowserNativeDeviceGateway } from './adapters/browser';
import { isNativeAppEmulationOn } from './emulation';
import type { NativeDeviceGateway } from './gateway';

let instance: Promise<NativeDeviceGateway> | null = null;

/**
 * What the resolved gateway says about the page, once detection has settled (BUG-2053). Set in
 * the same synchronous step that loads the adapter chunk, because loading `@capacitor/core`
 * defines `window.Capacitor` on a desktop page too, so the bridge check stops meaning "the app".
 */
let resolvedNative: boolean | null = null;

/** True while a gateway installed through the test seam answers in place of detection. */
let installedForTest = false;

/** Bumped by the test seam, so a detection still in flight from an earlier page settles nothing. */
let detection = 0;

/**
 * The active native-device gateway. Callers use this and never test for the
 * platform themselves (ADR 064 — Native Shell § 5).
 *
 * The Capacitor adapter is loaded on the first call rather than imported, so a
 * page that only renders a sign-in button ships none of `@capacitor/core`; the
 * chunk arrives when the user actually starts a native-capable action.
 *
 * @returns the Capacitor adapter inside the native app; outside production, the
 *   emulated adapter when `?emulate-native-app=1` is on (BL-2450); else the browser adapter.
 * @throws when the adapter chunk cannot be loaded (the next call retries).
 */
export function getNativeDeviceGateway(): Promise<NativeDeviceGateway> {
  if (instance) return instance;
  // A server render has no platform to detect. Answer "browser" without
  // memoising, so the page's first client-side call still detects the app.
  if (typeof window === 'undefined') return Promise.resolve(new BrowserNativeDeviceGateway());
  instance = detectGateway(detection).catch((err: unknown) => {
    // Don't pin a failed chunk load for the page; let the next call retry.
    instance = null;
    throw err;
  });
  return instance;
}

/**
 * A synchronous, chunk-free "could this page be the native app?" (BL-2342).
 * Capacitor's native bridge defines `window.Capacitor` before any page script
 * runs, so a page without it is a browser and needs no adapter chunk to say so.
 * True also in a non-production browser emulating the app (BL-2450), and once a
 * gateway is installed through the test seam, so the answer then comes from
 * `getNativeDeviceGateway()` as usual. Once detection has settled, the resolved
 * gateway answers: the root layout resolves it on every page before the page
 * reads, and a desktop page that resolved to the browser adapter is a browser,
 * whatever global the adapter chunk left behind (BUG-2053).
 *
 * @returns false only when the page certainly runs in a browser.
 */
export function mayBeNativeApp(): boolean {
  if (installedForTest) return true;
  if (resolvedNative != null) return resolvedNative;
  if (typeof window === 'undefined') return false;
  return 'Capacitor' in window || isNativeAppEmulationOn();
}

/** Records the detected gateway's answer, so {@link mayBeNativeApp} reads it from now on. */
function settle(gateway: NativeDeviceGateway, startedIn: number): NativeDeviceGateway {
  if (startedIn === detection) resolvedNative = gateway.isNativeApp();
  return gateway;
}

async function detectGateway(startedIn: number): Promise<NativeDeviceGateway> {
  // The real app always wins: emulation is consulted only on a page with no native bridge.
  if (!('Capacitor' in window) && isNativeAppEmulationOn()) {
    const { EmulatedNativeDeviceGateway } = await import('./adapters/emulated');
    return settle(new EmulatedNativeDeviceGateway(), startedIn);
  }
  const { CapacitorNativeDeviceGateway } = await import('./adapters/capacitor');
  const capacitor = new CapacitorNativeDeviceGateway();
  return settle(capacitor.isNativeApp() ? capacitor : new BrowserNativeDeviceGateway(), startedIn);
}

/**
 * Test seam: install a gateway (usually `MemoryNativeDeviceGateway`), or pass
 * `null` to restore detection. Exported only from `./testing`.
 *
 * @param gateway - the gateway to hand back, or null.
 */
export function __setNativeDeviceGatewayForTest(gateway: NativeDeviceGateway | null): void {
  instance = gateway ? Promise.resolve(gateway) : null;
  installedForTest = gateway != null;
  resolvedNative = null;
  detection += 1;
}
